import { createElement } from '../../adapters/dom';

export interface MethodCardData {
  methodCode: string;
  displayName?: string;
}

export interface CreateMethodCardOptions {
  method: MethodCardData;
  onSelect: (methodCode: string) => void;
}

function normalizeCode(value: string | null | undefined): string {
  return (value || '').trim().toUpperCase();
}

function parseMethodCode(methodCode: string): { networkCode: string; assetCode: string } {
  const parts = methodCode.split('_');
  const assetCode = normalizeCode(parts[parts.length - 1]);
  const networkCode = normalizeCode(parts.slice(0, -1).join('_'));

  return {
    networkCode,
    assetCode
  };
}

function getNetworkLabel(networkCode: string): string {
  const normalized = normalizeCode(networkCode);

  const labels: Record<string, string> = {
    SOLANA: 'Solana network',
    ETHEREUM: 'Ethereum network',
    TRON: 'TRON network'
  };

  return labels[normalized] ?? `${networkCode} network`;
}

function getCryptoLogoBaseUrl(): string {
  if (typeof window === 'undefined') {
    return 'https://pay.aviaxy.com/assets/crypto';
  }

  const customBase = (window as typeof window & {
    __CRYPTO_PAY_WIDGET_ASSET_BASE__?: string;
  }).__CRYPTO_PAY_WIDGET_ASSET_BASE__;

  return (customBase || 'https://pay.aviaxy.com/assets/crypto').replace(/\/+$/, '');
}

function getMethodLogoUrl(networkCode: string, assetCode: string): string {
  return `${getCryptoLogoBaseUrl()}/${networkCode.toLowerCase()}-${assetCode.toLowerCase()}.svg`;
}

function getAssetFallbackLogoUrl(assetCode: string): string {
  return `${getCryptoLogoBaseUrl()}/${assetCode.toLowerCase()}.svg`;
}

export function createMethodCard(options: CreateMethodCardOptions): HTMLButtonElement {
  const button = createElement('button', {
    className: 'cpw-method-card',
    attributes: {
      type: 'button',
      'data-method-code': options.method.methodCode
    }
  }) as HTMLButtonElement;

  const { networkCode, assetCode } = parseMethodCode(options.method.methodCode);

  const logoWrap = createElement('span', {
    className: 'cpw-method-card-logo-wrap'
  }) as HTMLSpanElement;

  const logo = createElement('img', {
    className: 'cpw-method-card-logo',
    attributes: {
      src: getMethodLogoUrl(networkCode, assetCode),
      alt: `${assetCode} on ${networkCode}`,
      width: '32',
      height: '32',
      loading: 'lazy'
    }
  }) as HTMLImageElement;

  const logoFallback = createElement('span', {
    className: 'cpw-method-card-logo-fallback',
    textContent: assetCode.slice(0, 4)
  });

  let triedAssetFallback = false;

  logo.addEventListener('error', () => {
    if (!triedAssetFallback) {
      triedAssetFallback = true;
      logo.src = getAssetFallbackLogoUrl(assetCode);
      return;
    }

    logo.remove();
    logoWrap.append(logoFallback);
  });

  logoWrap.append(logo);

  const content = createElement('span', {
    className: 'cpw-method-card-content'
  }) as HTMLSpanElement;

  const title = createElement('span', {
    className: 'cpw-method-card-title',
    textContent: networkCode ? `${assetCode} · ${networkCode}` : options.method.methodCode
  });

  const code = createElement('span', {
    className: 'cpw-method-card-code',
    textContent: networkCode ? getNetworkLabel(networkCode) : ''
  });

  content.append(title, code);

  const arrow = createElement('span', {
    className: 'cpw-method-card-arrow',
    textContent: '→'
  });

  button.append(logoWrap, content, arrow);

  button.addEventListener('click', () => {
    options.onSelect(options.method.methodCode);
  });

  return button;
}